import {
  Button,
  Checkbox,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridRowKind,
  GridTable,
  IconButton,
  LoadingSkeleton,
  PageSettings,
  Pagination,
  ScrollableParent,
  Switch,
  column,
  emptyCell,
  multiFilter,
  selectColumn,
  simpleHeader,
  useComputed,
  useFilter,
  useModal,
} from "@homebound/beam";
import xor from "lodash/xor";
import { useCallback, useState } from "react";
import { SearchBox } from "src/components";
import { StepActions, useStepperContext } from "src/components/stepper";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidItemFilter, BidPackageStatus, CostType, Order } from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import { useSaveBidPackagesMutation } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { ReviewScopeStep_BidPackageFragment } from "src/routes/bids/add-bid-package/components/2.ReviewUnitBidScopeStep.generated";
import {
  BidMetaQuery,
  BidPackageLineItemModalQuery,
  EditBidPackageLineItemModal_LineItemFragment,
  useBidMetaQuery,
  useBidPackageLineItemModalQuery,
  useEditBidMetaQuery,
} from "src/routes/bids/add-bid-package/components/BidPackageLineItemModal.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { TableActions } from "src/routes/layout/TableActions";
import { pluralize, queryResult } from "src/utils";
import { z } from "zod";
import { PageBidPackageDetailFragment } from "../../BidPackageDetailPage.generated";

export type BidPackageLineItemParams = Omit<BidItemFilter, "search"> & PageSettings;

type BidPackageLineItemModalProps = { bidPackage: ReviewScopeStep_BidPackageFragment };

export function BidPackageLineItemModal({ bidPackage }: Pick<BidPackageLineItemModalProps, "bidPackage">) {
  // Fetch cost types and items (material category) for the bidItem filter options
  const metaQuery = useBidMetaQuery({
    variables: {
      itemFilter: {
        // Ensure the material category filter only shows items with matching bp costcodes
        costCode: bidPackage.costCodes
          .map((cc) => cc.id)
          .unique()
          .compact(),
        version: [1, 2],
      },
    },
  });

  return queryResult(metaQuery, (data) => (
    <BidPackageLineItemView {...data} bidPackage={bidPackage} lineItems={bidPackage.lineItems} />
  ));
}

type EditBidPackageLineItemModalProps = { bidPackage: PageBidPackageDetailFragment };

export function EditBidPackageLineItemModal({ bidPackage }: EditBidPackageLineItemModalProps) {
  // Fetch cost types and items (material category) for the bidItem filter options
  const metaQuery = useEditBidMetaQuery({
    variables: {
      bidPackageVersionId: bidPackage.latestVersion.id,
      itemFilter: {
        // Ensure the material category filter only shows items with matching bp costcodes
        costCode: bidPackage.costCodes
          .map((cc) => cc.id)
          .unique()
          .compact(),
        version: [1, 2],
      },
    },
  });

  return queryResult(metaQuery, ({ bidPackageVersion, ...data }) => (
    <BidPackageLineItemView
      {...data}
      bidPackage={bidPackage}
      lineItems={bidPackageVersion.lineItems}
      previousLineItems={bidPackageVersion.previous?.lineItems}
      isEdit
    />
  ));
}

type BidPackageLineItemViewProps = {
  costTypes: BidMetaQuery["costTypes"];
  items: NamedFragment[];
  bidPackage: ReviewScopeStep_BidPackageFragment | PageBidPackageDetailFragment;
  isEdit?: boolean;
  materialVariantTags: NamedFragment[];
  lineItems: EditBidPackageLineItemModal_LineItemFragment[];
  previousLineItems?: EditBidPackageLineItemModal_LineItemFragment[];
};

export function BidPackageLineItemView(props: BidPackageLineItemViewProps) {
  const { costTypes, items, bidPackage, materialVariantTags, isEdit, lineItems, previousLineItems = [] } = props;
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const { closeModal, openModal } = useModal();
  const { steps } = useStepperContext();
  const [saveBidPackages] = useSaveBidPackagesMutation({
    // Since we're in a modal overlay ensure we get the latest of any edits to the line items in the review step or details page
    refetchQueries: [
      "ReviewUnitBidScopeStep_BidPackageGroup",
      "ReviewScopeStep_BidPackageGroup",
      "BidPackageDetailPage",
      "UnitBasedBidsTable",
      "PlanBasedBidsTable",
    ],
  });
  const { setFilter, filter: bidItemsFilter } = useFilter<BidItemFilter>({
    filterDefs: filterDefs(costTypes, items, materialVariantTags),
  });
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [isUsedUpstream, setIsUsedUpstream] = useState<boolean>(bidPackage.isUnitBased);
  const [selectedBidItemIds, setSelectedBidItemIds] = useState<string[]>(lineItems.map((li) => li.bidItem.id));

  const { data, loading } = useBidPackageLineItemModalQuery({
    variables: {
      filter: {
        bidPackageId: bidPackage.id,
        isUsedUpstream,
        bidItemsFilter: { ...bidItemsFilter, search: searchFilter },
      },
      order: { name: Order.Desc },
      offset: pageSettings.offset,
      first: pageSettings.limit,
    },
  });
  const hasNoEdits = useComputed(() => {
    return (
      xor(
        // Compare if line items have changed
        lineItems.map((li) => li.id),
        lineItems.filter((li) => selectedBidItemIds.includes(li.bidItem.id)).map((li) => li.id),
        // Compare if new bid items have been added
        lineItems.map((li) => li.bidItem.id),
        selectedBidItemIds,
      ).isEmpty && "No new edits made"
    );
  }, [selectedBidItemIds]);

  const saveAndExit = useCallback(async () => {
    await saveBidPackages({
      variables: {
        input: [
          {
            id: bidPackage.id,
            lineItems: [
              // Persist line item ids that weren't deselected
              ...lineItems.filter((li) => selectedBidItemIds.includes(li.bidItem.id)).map((li) => ({ id: li.id })),
              // Add line items that were newly selected
              ...selectedBidItemIds
                .filter((id) => !lineItems.some((li) => li.bidItem.id === id))
                .map((id) => ({ bidItemId: id })),
            ],
          },
        ],
      },
    });
    closeModal();
  }, [bidPackage.id, lineItems, saveBidPackages, selectedBidItemIds, closeModal]);

  const handleSave = useCallback(async () => {
    // Skip the confirmation modal for plan-based V2 bid packages since they don't create new versions
    if (bidPackage.status.code !== BidPackageStatus.Draft && !bidPackage.isPlanBasedV2) {
      openModal({
        content: (
          <ConfirmationModal
            title="Save Line Item Changes"
            confirmationMessage="Editing line items on a package will create a new version. Are you sure you want to continue?"
            onConfirmAction={saveAndExit}
            label="Yes"
          />
        ),
      });
    } else {
      await saveAndExit();
    }
  }, [bidPackage, openModal, saveAndExit]);

  return (
    <ScrollableParent xss={Css.h("calc(100% - 144px)").$}>
      <header data-testid="bidLineItemsModal" css={Css.pb4.pt2.bb.bw1.bcGray200.bgWhite.$}>
        <div css={Css.df.fdc.$}>
          <div css={Css.ml("97%").$}>
            <IconButton icon="x" onClick={closeModal} />
          </div>
          <div css={Css.df.fdc.aic.jcc.$}>
            <h1 css={Css.xl2.$}>{bidPackage.name}</h1>
            <div css={Css.py1.$}>You can toggle to view items used upstream by plans or design packages</div>
          </div>
        </div>
      </header>
      <div css={Css.my1.mxPx(178).$}>
        <div css={Css.df.fdc.$}>
          {bidPackage.isUnitBased && (
            <div css={Css.df.bb.bw1.bcGray200.pb2.pt1.mb3.$}>
              <Switch
                label={
                  bidPackage.isProjectLevelBidding
                    ? "Show items used by the Projects"
                    : bidPackage.latestVersion.version === 1
                      ? "Show items used by upstream plan and design packages"
                      : "Show items used by offerings"
                }
                selected={isUsedUpstream}
                onChange={setIsUsedUpstream}
              />
            </div>
          )}
          <TableActions>
            <div css={Css.df.fdr.cg1.if(!bidPackage.isUnitBased).my2.$}>
              <Filters<BidItemFilter>
                filter={bidItemsFilter}
                filterDefs={filterDefs(costTypes, items, materialVariantTags)}
                onChange={setFilter}
                numberOfInlineFilters={8}
              />
            </div>
            <div>
              <SearchBox onSearch={setSearchFilter} clearable debounceDelayInMs={500} />
            </div>
          </TableActions>
        </div>
        {loading ? (
          <>
            <LoadingSkeleton rows={1} columns={1} />
            <LoadingSkeleton rows={5} columns={5} />
          </>
        ) : (
          <GridTable
            columns={createColumns(previousLineItems, setSelectedBidItemIds)}
            rows={createRows(data?.potentialBidItemsPage.bidItems, selectedBidItemIds)}
            style={{ bordered: true, allWhite: true }}
            stickyHeader
          />
        )}
        <Pagination
          page={[pageSettings, setPageSettings]}
          totalCount={data?.potentialBidItemsPage.pageInfo.totalCount || 0}
        />
      </div>
      {/* Only render StepActions when we're in a stepper context (steps array is non-empty). PBBv2 uses this page standalone, not in a stepper, so it falls back to the fixed bottom bar below. */}
      {isEdit && steps.nonEmpty ? (
        <StepActions>
          <>
            <div css={Css.mya.gray600.wsnw.$}>
              {selectedBidItemIds.length} {pluralize(selectedBidItemIds, "item")} selected
            </div>
            <Button label="Save" onClick={handleSave} size="lg" disabled={hasNoEdits} />
          </>
        </StepActions>
      ) : (
        <div css={Css.fixed.bottom0.w100.px6.py3.bshHover.df.bgWhite.$}>
          <div css={Css.mla.df.gap2.$}>
            <div css={Css.mya.gray600.$}>
              {selectedBidItemIds.length} {pluralize(selectedBidItemIds, "item")} selected
            </div>
            <Button label="Save" onClick={handleSave} size="lg" disabled={hasNoEdits} />
          </div>
        </div>
      )}
    </ScrollableParent>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = {
  kind: "data";
  data: {
    costCode: string | undefined;
    materialCode: string | undefined;
    materialVariantName: string;
    uom: string;
    isSelected: boolean;
  };
};
type Row = HeaderRow | DataRow;

function createRows(
  bidItems: BidPackageLineItemModalQuery["potentialBidItemsPage"]["bidItems"] | undefined,
  selectedBidItemIds: string[],
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...(bidItems || []).map((bi) => ({
      id: bi.id,
      kind: "data" as const,
      data: {
        costCode: bi.items[0].costCode.displayName,
        materialCode: bi.parentMaterialVariant?.code,
        materialVariantName:
          bi.costType.code === CostType.Labor
            ? (bi.parentTask?.name ?? bi.name)
            : (bi.parentMaterialVariant?.displayName ?? bi.displayName),
        uom: bi.unitOfMeasure.abbreviation,
        id: bi.id,
        isSelected: selectedBidItemIds.includes(bi.id),
      },
    })),
  ];
}

function createColumns(
  previousLineItems: EditBidPackageLineItemModal_LineItemFragment[],
  setSelectedBidItemIds: React.Dispatch<React.SetStateAction<string[]>>,
): GridColumn<Row>[] {
  return [
    selectColumn<Row>({
      header: emptyCell,
      data: (bi, { row }) => (
        <Checkbox
          checkboxOnly={true}
          label="Select"
          onChange={(selected) => {
            if (selected) {
              setSelectedBidItemIds((prev) => [...prev, row.id]);
            } else {
              setSelectedBidItemIds((prev) => prev.filter((id) => id !== row.id));
            }
          }}
          selected={bi.isSelected}
        />
      ),
    }),
    column<Row>({
      header: "Cost Code",
      data: (bi, { row }) => ({
        content: bi.costCode,
        css: Css.if(shouldHighlightRow(row, previousLineItems)).bgYellow50.$,
      }),
      w: 1,
    }),
    column<Row>({
      header: "Name",
      data: (bi, { row }) => ({
        content: bi.materialVariantName,
        css: Css.if(shouldHighlightRow(row, previousLineItems)).bgYellow50.$,
      }),
      w: 1.5,
    }),
    column<Row>({
      header: "Uom",
      data: (bi, { row }) => ({
        content: bi.uom,
        css: Css.if(shouldHighlightRow(row, previousLineItems)).bgYellow50.$,
      }),
      w: 0.5,
    }),
    column<Row>({
      header: "Material code",
      data: (bi, { row }) => ({
        content: bi.materialCode,
        css: Css.if(shouldHighlightRow(row, previousLineItems)).bgYellow50.$,
      }),
      w: 1,
    }),
  ];
}

// We should highlight the row if it's selected and it wasn't in the last version that exists
function shouldHighlightRow(
  row: GridRowKind<Row, "data">,
  previousLineItems: EditBidPackageLineItemModal_LineItemFragment[],
): boolean {
  return (
    previousLineItems.nonEmpty &&
    !previousLineItems.some(({ bidItem }) => bidItem.id === row.id) &&
    row.data?.isSelected
  );
}

function filterDefs(
  costTypes: BidMetaQuery["costTypes"],
  items: NamedFragment[],
  tags: NamedFragment[],
): FilterDefs<BidItemFilter> {
  return {
    costType: multiFilter({
      label: "Cost Type",
      options: costTypes,
      getOptionValue: (o) => o.code,
      getOptionLabel: (o) => o.name,
    }),
    items: multiFilter({
      label: "Material Category",
      options: items,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    }),
    materialVariantTags: multiFilter({
      label: "Material Variant Tags",
      options: tags,
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    }),
  };
}

const pageSchema = z.object({
  offset: z.coerce.number().int().default(0),
  limit: z.coerce.number().int().positive().default(500),
});
